/**
 * The local replica on disk.
 *
 * WHY
 *   Lattice must be useful the instant it opens, before any network call
 *   returns. So the whole workspace — every change, every comment — is kept in
 *   IndexedDB and read back on start. A reload is a local read, not a request:
 *   the file tree is on screen in single-digit milliseconds.
 *
 * DATA DEFINITION
 *   A Snapshot is { changes, comments, cursor, projects, savedAt }.
 *   Interpretation: everything the client knew as of sequence number `cursor`.
 *   Because the log is append-only and the cursor is a single integer,
 *   catching up after being offline is one request for the tail.
 *
 * ON FAILURE
 *   Every operation here is best-effort. A browser in private mode, with site
 *   data cleared, or out of quota, must not break the application — it just
 *   makes the first paint wait for the network. Nothing here ever throws.
 */
import { openDB, type IDBPDatabase } from 'idb'
import type { Change, Comment, Lock } from '@lattice/core'
import type { ProjectDto } from './api'

export interface Snapshot {
  readonly changes: Change[]
  readonly comments: Comment[]
  readonly locks: Lock[]
  readonly projects: ProjectDto[]
  readonly cursor: number
  /**
   * The workspace epoch this cursor belongs to.
   *
   * A cursor is only meaningful against the log it was read from. Storing the
   * epoch beside it is what lets `boot` notice that the server's log has been
   * rebuilt and throw this replica away, instead of asking forever for
   * sequence numbers that will never be issued.
   */
  readonly epoch?: string
  readonly savedAt: number
}

const DB_NAME = 'lattice'
const DB_VERSION = 2
const STORE = 'replica'
const KEY = 'snapshot'

let handle: Promise<IDBPDatabase> | undefined

/**
 * How long to wait for the database before giving up on the cache.
 *
 * An upgrade cannot proceed while another tab holds the old version open, and
 * that other tab may be one the person forgot about days ago. Waiting forever
 * would mean boot() never finishes and the product never appears — a far worse
 * outcome than starting from the network. So we bound it.
 */
const OPEN_TIMEOUT_MS = 3_000

/** Open (and if necessary create) the local database. */
function db(): Promise<IDBPDatabase> {
  handle ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains(STORE)) database.createObjectStore(STORE)
    },
    /**
     * Another tab is holding an older version open, so our upgrade is stuck.
     * There is nothing to do but say so; the timeout below is what actually
     * rescues the boot.
     */
    blocked() {
      console.warn('Lattice: another tab is holding an older local database open; starting from the network instead.')
    },
    /**
     * A NEWER tab wants to upgrade and we are in its way. Close, so it can.
     * This tab keeps working from memory and will re-open on its next write.
     */
    blocking() {
      void handle?.then(database => database.close()).catch(() => undefined)
      handle = undefined
    },
    terminated() {
      // The browser closed the connection underneath us (tab discarded, disk
      // pressure). Forget it so the next call opens a fresh one.
      handle = undefined
    },
  })
  return handle
}

/**
 * The database, or undefined if it does not open promptly.
 *
 * Every caller treats persistence as best-effort, so a slow or blocked open
 * degrades to "no cache" rather than to a product that never starts.
 * dbOrGiveUp : -> Promise<IDBPDatabase|undefined>
 */
async function dbOrGiveUp(): Promise<IDBPDatabase | undefined> {
  try {
    return await Promise.race([
      db(),
      new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), OPEN_TIMEOUT_MS)),
    ])
  } catch {
    return undefined
  }
}

/**
 * Read the stored replica, or undefined when there is none.
 * loadSnapshot : -> Promise<Snapshot|undefined>
 */
export async function loadSnapshot(): Promise<Snapshot | undefined> {
  try {
    const database = await dbOrGiveUp()
    if (!database) return undefined
    const snapshot = (await database.get(STORE, KEY)) as Snapshot | undefined
    // A snapshot from a future schema, or a corrupt one, is simply ignored.
    if (!snapshot || !Array.isArray(snapshot.changes)) return undefined
    return snapshot
  } catch {
    return undefined
  }
}

/**
 * Write the replica.
 * saveSnapshot : Snapshot -> Promise<Boolean>   (did it persist?)
 */
export async function saveSnapshot(snapshot: Omit<Snapshot, 'savedAt'>): Promise<boolean> {
  try {
    const database = await dbOrGiveUp()
    if (!database) return false
    await database.put(STORE, { ...snapshot, savedAt: Date.now() }, KEY)
    return true
  } catch {
    return false
  }
}

/** Forget everything. Used on sign-out, so one person's replica never leaks to the next. */
export async function clearSnapshot(): Promise<void> {
  try {
    const database = await dbOrGiveUp()
    if (database) await database.delete(STORE, KEY)
  } catch { /* nothing to clear */ }
}
